import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { ApiError, apiErrorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatInrMinor } from '../../lib/constants';
import { AppShell } from '../components/AppShell';
import { ExamFeeNotice } from '../components/ExamFeeNotice';
import { StatusBadge } from '../components/StatusBadge';
import {
  addBagItem,
  fetchApplication,
  fetchBag,
  fetchOfferingSubjects,
  removeBagItem,
  requestBagEstimate,
  submitSubjectSelection,
  type FeeEstimateView,
} from '../lib/bag-api';
import { fetchMyLearners } from '../lib/registration-api';
import { dashboardPath, pathForApplication } from '../lib/selected-learner';

const MIN_SUBJECTS_FALLBACK = 1;
const MAX_SUBJECTS_FALLBACK = 6;

/**
 * Items that are part of the selection being submitted. Approved states appear
 * on a bag a later reviewer returned: they carry into the next version and the
 * parent cannot drop them, so they read as selected but locked.
 */
const SELECTED_ITEM_STATUSES = [
  'REQUESTED',
  'APPROVED_21K',
  'APPROVED_PARTNER',
  'APPROVED_VIA_BYPASS',
];

/**
 * The engine's descriptions are written for a quotation PDF ("Physics Tuition —
 * IGCSE Year 1"), which is too long for a summary column and repeats the group
 * heading above it. Here the subject name alone identifies the line.
 */
function estimateLineLabel(
  line: { description: string; subjectCode: string | null },
  groupLabel: string,
  subjectNames: Map<string, string>,
): string {
  if (line.subjectCode) {
    return subjectNames.get(line.subjectCode) ?? line.description;
  }
  const prefix = `${groupLabel} — `;
  return line.description.startsWith(prefix)
    ? line.description.slice(prefix.length)
    : line.description;
}

export function SubjectSelectionPage(): ReactElement {
  const { applicationId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<FeeEstimateView | null>(null);
  const [estimateBusy, setEstimateBusy] = useState(false);

  const applicationQuery = useQuery({
    queryKey: ['application', applicationId],
    queryFn: () => fetchApplication(applicationId),
    enabled: Boolean(token && applicationId),
  });

  const bagId = applicationQuery.data?.academicBagId ?? null;
  const offeringId = applicationQuery.data?.offeringId ?? null;

  const bagQuery = useQuery({
    queryKey: ['bag', bagId],
    queryFn: () => fetchBag(bagId!),
    enabled: Boolean(token && bagId),
  });

  const subjectsQuery = useQuery({
    queryKey: ['offering-subjects', offeringId, bagQuery.data?.academicLevelId],
    queryFn: () => fetchOfferingSubjects(offeringId!, bagQuery.data!.academicLevelId),
    enabled: Boolean(token && offeringId && bagQuery.data?.academicLevelId),
  });

  const subjectsView = subjectsQuery.data;
  const subjectGroups = subjectsView?.groups ?? [];
  const allSubjects = useMemo(
    () => subjectGroups.flatMap((group) => group.subjects),
    [subjectGroups],
  );
  const minSubjects = subjectsView?.selectionRule?.minSubjects ?? MIN_SUBJECTS_FALLBACK;
  const maxSubjects = subjectsView?.selectionRule?.maxSubjects ?? MAX_SUBJECTS_FALLBACK;

  const subjectNames = useMemo(
    () => new Map(allSubjects.map((s) => [s.subjectCode, s.subjectName])),
    [allSubjects],
  );

  const selectedIds = useMemo(
    () => new Set(
      (bagQuery.data?.items ?? [])
        .filter((item) => SELECTED_ITEM_STATUSES.includes(item.itemStatus))
        .map((item) => item.catalogueSubjectId),
    ),
    [bagQuery.data?.items],
  );

  const lockedIds = useMemo(
    () => new Set(
      (bagQuery.data?.items ?? [])
        .filter((item) => SELECTED_ITEM_STATUSES.includes(item.itemStatus)
          && item.itemStatus !== 'REQUESTED')
        .map((item) => item.catalogueSubjectId),
    ),
    [bagQuery.data?.items],
  );

  const refreshEstimate = useCallback(async (id: string) => {
    setEstimateBusy(true);
    try {
      const result = await requestBagEstimate(id);
      setEstimate(result);
    } catch {
      setEstimate(null);
    } finally {
      setEstimateBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!bagId || selectedIds.size === 0) {
      setEstimate(null);
      return;
    }
    void refreshEstimate(bagId);
  }, [bagId, selectedIds.size, refreshEstimate]);

  const toggleMutation = useMutation({
    mutationFn: async (catalogueSubjectId: string) => {
      if (!bagId || !bagQuery.data) throw new Error('Bag not ready');
      const existing = bagQuery.data.items.find(
        (item) => item.catalogueSubjectId === catalogueSubjectId && item.itemStatus === 'REQUESTED',
      );
      if (existing) {
        await removeBagItem(bagId, existing.id);
      } else {
        await addBagItem(bagId, catalogueSubjectId);
      }
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['bag', bagId] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? apiErrorMessage(err.problem) : 'Could not update selection');
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => submitSubjectSelection(applicationId),
    onSuccess: async () => {
      void queryClient.invalidateQueries({ queryKey: ['my-learners'] });
      const learners = await fetchMyLearners();
      navigate(pathForApplication(learners, applicationId));
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? apiErrorMessage(err.problem) : 'Could not submit selection');
    },
  });

  if (!loading && !token) return <Navigate to="/login" replace />;
  if (!applicationId) return <Navigate to={dashboardPath()} replace />;

  const application = applicationQuery.data;
  const canEdit = application?.status === 'REGISTERED' || application?.status === 'CHANGES_REQUESTED';

  if (applicationQuery.isSuccess && application && !canEdit) {
    return (
      <AppShell title="Choose subjects">
        <div className="notice notice-info">
          <strong>Subject selection is not available</strong>
          <p>
            This application is <StatusBadge status={application.status} />.
            Return to your dashboard for next steps.
          </p>
          <Link className="btn btn-secondary" to="/dashboard">Back to dashboard</Link>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell width="wide" title="Choose subjects">
      <div className="page-header">
        <p className="lede">
          Select the subjects your child will study. The estimated fee updates as you choose.
        </p>
      </div>

      {error ? <div className="error-banner" role="alert">{error}</div> : null}

      {(applicationQuery.isLoading || bagQuery.isLoading || subjectsQuery.isLoading) ? (
        <p className="text-muted">Loading subjects…</p>
      ) : null}

      {subjectsView && allSubjects.length === 0 ? (
        <div className="notice notice-info">
          <strong>No subjects available yet</strong>
          <p>There are no subjects configured for this grade in your school&apos;s offering.</p>
        </div>
      ) : null}

      <div className="subject-layout">
        <section className="card">
          <header className="subject-picker-header">
            <div>
              <h2 className="h3">Available subjects</h2>
              <p className="subject-picker-hint">
                Choose between {minSubjects} and {maxSubjects} subjects. Your selection saves automatically.
              </p>
            </div>
            <span className="subject-count" aria-live="polite">
              {selectedIds.size} of {maxSubjects} selected
            </span>
          </header>

          {subjectGroups.map((group) => (
            <section key={group.subjectGroupId ?? group.subjectGroupName} className="subject-group-section">
              <h3 className="subject-group-heading">{group.subjectGroupName}</h3>
              <ul className="subject-list">
                {group.subjects.map((subject) => {
                  const selected = selectedIds.has(subject.catalogueSubjectId);
                  const locked = lockedIds.has(subject.catalogueSubjectId);
                  return (
                    <li key={subject.catalogueSubjectId}>
                      <button
                        type="button"
                        className={`subject-card${selected ? ' subject-card-selected' : ''}`}
                        disabled={toggleMutation.isPending || !canEdit || locked}
                        aria-pressed={selected}
                        onClick={() => toggleMutation.mutate(subject.catalogueSubjectId)}
                      >
                        <span className="subject-card-check" aria-hidden="true" />
                        <span className="subject-card-text">
                          <span className="subject-card-name">{subject.subjectName}</span>
                          <span className="subject-card-code">
                            {locked ? (
                              'Already approved — stays in your selection'
                            ) : (
                              <span className="subject-syllabus-code">{subject.cambridgeSyllabusCode}</span>
                            )}
                          </span>
                        </span>
                        <span className="subject-card-price money">
                          {subject.priceMinor ? formatInrMinor(subject.priceMinor) : '—'}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </section>

        <aside className="fee-panel card">
          <h2 className="h3">Fee summary</h2>
          <p className="fee-panel-note">
            Non-binding estimate. Final fees are confirmed after approval.
          </p>

          <div className="estimate-body" aria-live="polite" aria-busy={estimateBusy}>
            {selectedIds.size === 0 ? (
              <p className="estimate-empty">
                Select a subject to see the estimated fee.
              </p>
            ) : (
              (estimate?.groups ?? []).map((group) => (
                <div key={group.label} className="estimate-group">
                  <div className="estimate-group-header">
                    <span className="estimate-group-label">{group.label}</span>
                    <span className="estimate-group-total money">{group.totalDisplay}</span>
                  </div>
                  <ul className="estimate-lines">
                    {group.lines.map((line) => (
                      <li key={line.lineKey} className="estimate-line">
                        <span className="estimate-line-label">
                          {estimateLineLabel(line, group.label, subjectNames)}
                        </span>
                        <span className="estimate-line-amount money">
                          {formatInrMinor(line.netAmountMinor)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>

          <div className="summary-total">
            <span className="summary-total-label">Estimated total</span>
            <span className="summary-total-value">
              {estimateBusy
                ? 'Calculating…'
                : (estimate?.payableTotalDisplay ?? (selectedIds.size ? '—' : '₹0.00'))}
            </span>
          </div>

          <ExamFeeNotice text={estimate?.notices?.find((n) => n.code === 'CAMBRIDGE_EXAM_FEE_NOTE')?.text} />
        </aside>
      </div>

      <div className="form-actions">
        <Link className="btn btn-ghost" to="/dashboard">Save and exit</Link>
        <button
          className="btn btn-lg"
          type="button"
          disabled={submitMutation.isPending || selectedIds.size === 0 || !canEdit}
          onClick={() => submitMutation.mutate()}
        >
          {submitMutation.isPending ? 'Submitting…' : 'Submit for approval'}
        </button>
      </div>
    </AppShell>
  );
}
